import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAccountByEmail, createAccount } from '../services/accountService';
import { TextField, Button, Box, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { AccountCircle } from '@mui/icons-material';
import { createSubscription } from '../services/subscriptionService';
import ReCAPTCHA from 'react-google-recaptcha';
import CONFIG from '../config/config';

const SignUp = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const selectedPlan = location.state?.selectedPlan || 'Free';

    const [formValues, setFormValues] = useState({
        accountName: '',
        contact: '',
        email: '',
        agreeToTerms: false,
        recaptchaVerified: false,
    });

    const [errors, setErrors] = useState({ email: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormValues((prev) => ({ ...prev, [name]: value }));

        // Validate email
        if (name === 'email') {
            setErrors((prev) => ({
                ...prev,
                email: emailRegex.test(value) ? '' : 'Please enter a valid email address',
            }));
        }
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormValues((prev) => ({ ...prev, [name]: checked }));
    };

    const handleRecaptcha = (value) => {
        setFormValues((prev) => ({ ...prev, recaptchaVerified: !!value }));
    };

    const isFormComplete =
        formValues.accountName &&
        formValues.contact &&
        formValues.email &&
        !errors.email && // Ensure no email error
        formValues.agreeToTerms &&
        formValues.recaptchaVerified;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isFormComplete) return;

        setIsSubmitting(true);

        try {
            // Check if the email is already used
            const existingAccount = await getAccountByEmail(formValues.email);
            if (existingAccount) {
                setErrors((prev) => ({
                    ...prev,
                    email: 'This email is already in use. Please use a different email.',
                }));
                setIsSubmitting(false);
                return; // Stop submission
            }

            // Create the account
            try {
                const accountData = {
                    accountName: formValues.accountName,
                    contact: formValues.contact,
                    email: formValues.email,
                };

                const response = await createAccount(accountData);
                if (response && response.accountGuid) {
                    const accountGuid = response.accountGuid;

                    console.log('Account created successfully:', { accountGuid });

                    // Sign in the user
                    localStorage.setItem(
                        'authSession',
                        JSON.stringify({
                            email: formValues.email,
                            accountGuid: accountGuid,
                            isVerified: true,
                        })
                    );

                    // Create a subscription if the selected plan is 'free' or 'pro'
                    if (['free', 'pro'].includes(selectedPlan.toLowerCase())) {
                        try {
                            const subscriptionData = {
                                accountGuid,
                                planId: selectedPlan.toLowerCase(),
                                orgName: formValues.accountName,
                            };

                            const subscriptionResponse = await createSubscription(subscriptionData);
                            if (subscriptionResponse && subscriptionResponse.subscriptionGuid) {
                                const subscriptionGuid = subscriptionResponse.subscriptionGuid;
                                console.log('Subscription created successfully:', { subscriptionGuid });

                                // Navigate to the Subscribe page
                                navigate('/subscribe', {
                                    state: { selectedPlan, accountGuid, subscriptionGuid },
                                });
                                return;
                            } else {
                                console.error('Failed to create subscription or missing subscriptionGuid in response');
                            }
                        } catch (error) {
                            console.error('Error during subscription creation:', error);
                            setErrors((prev) => ({
                                ...prev,
                                form: 'Failed to create subscription. Please try again later.',
                            }));
                        }
                    } else {
                        console.log('Plan does not require immediate subscription creation.');
                        navigate('/subscribe', { state: { selectedPlan, accountGuid } });
                    }
                } else {
                    console.error('Failed to create account or missing accountGuid in response');
                }
            } catch (error) {
                console.error('Error during account creation:', error);
                setErrors((prev) => ({
                    ...prev,
                    form: 'Failed to create account. Please try again later.',
                }));
            } finally {
                setIsSubmitting(false);
            }

            setIsSubmitting(false);
        } catch (error) {
            console.error('Error checking email:', error);
            setErrors((prev) => ({
                ...prev,
                email: 'An error occurred while checking the email. Please try again.',
            }));
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                maxWidth: 400,
                margin: '0 auto',
                padding: 4,
                borderRadius: 2,
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                textAlign: 'center',
            }}
        >
            <AccountCircle sx={{ fontSize: 64, color: '#1976d2', marginBottom: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
                Sign Up
            </Typography>
            <Typography variant="body1" gutterBottom>
                Let's create your user account to get started with the{' '}
                <Typography component="span" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                    {selectedPlan}
                </Typography>{' '}
                plan.
            </Typography>
            <form onSubmit={handleSubmit}>
                <TextField
                    label="Account Name"
                    name="accountName"
                    value={formValues.accountName}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    required
                    autoComplete="off"
                />
                <TextField
                    label="Contact Person"
                    name="contact"
                    value={formValues.contact}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    required
                    autoComplete="off"
                />
                <TextField
                    label="Email"
                    name="email"
                    type="email"
                    value={formValues.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    fullWidth
                    margin="normal"
                    required
                    autoComplete="off"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            name="agreeToTerms"
                            checked={formValues.agreeToTerms}
                            onChange={handleCheckboxChange}
                        />
                    }
                    label={
                        <Typography variant="body2">
                            I agree to Instapath's{' '}
                            <a href="/terms-of-use" target="_blank" rel="noopener noreferrer">
                                Terms and Policies
                            </a>
                        </Typography>
                    }
                    sx={{
                        textAlign: 'left',
                        justifyContent: 'flex-start',
                        display: 'flex',
                        marginTop: 1,
                        width: '100%',
                        '& .MuiFormControlLabel-asterisk': { display: 'none' },
                    }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                    <ReCAPTCHA
                        sitekey={CONFIG.GOOGLE.SITE_KEY_V2}
                        onChange={handleRecaptcha}
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                    <Button
                        onClick={() => navigate(-1)}
                        variant="outlined"
                        color="secondary"
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!isFormComplete || isSubmitting}
                    >
                        Register
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default SignUp;